import React from "react";
import { Box, Flex, Text, Badge, Avatar, Spinner, Icon, useColorModeValue } from "@chakra-ui/react";
import { MdSearch, MdPersonSearch, MdGroups } from "react-icons/md";
import { TAB_ROLE_TAG } from "./usersListConstants";

/**
 * Round accent monogram for user rows (lightweight rows carry no photo).
 * @param {Object} props - { label, accent }
 * @returns {React.ReactElement}
 */
function RowAvatar({ label, accent }) {
    return (
        <Flex align="center" justify="center" w="38px" h="38px" borderRadius="12px" flexShrink={0}
            bg={`${accent}1F`} color={accent} fontWeight="800" fontSize="1rem">
            {label}
        </Flex>
    );
}

/**
 * Builds the secondary line + tag for a group row.
 * @param {Object} group - Group object with members
 * @returns {{secondary: string}}
 */
function groupSecondary(group) {
    const members = group.members || [];
    const leader = members.find((m) => m?.is_leader);
    const leaderName = leader ? ` · ${leader.surname || leader.email || `#${leader.id}`}` : "";
    return { secondary: `${members.length} membre(s)${leaderName}` };
}

/**
 * One selectable row, rendering either a user or a group.
 * @param {Object} props - { item, mode, isSelected, onSelect, roleTag, palette }
 * @returns {React.ReactElement}
 */
function EntityRow({ item, mode, isSelected, onSelect, roleTag, palette }) {
    const isGroup = mode === "groups";
    const accent = (isGroup ? "#FFB547" : roleTag?.color) || "#422AFB";
    const primary = item.name || (isGroup ? "Sans nom" : "Sans nom");
    const secondary = isGroup ? groupSecondary(item).secondary : item.email;
    const tag = isGroup ? { label: "Groupe", bg: "rgba(255,181,71,0.12)", color: "#B26A00", border: "rgba(255,181,71,0.35)" } : roleTag;
    return (
        <Flex align="center" gap="12px" p="10px" borderRadius="14px" cursor="pointer" role="button"
            bg={isSelected ? palette.selectedBg : "transparent"} transition="background 0.15s ease"
            _hover={{ bg: isSelected ? palette.selectedBg : palette.hoverBg }} onClick={() => onSelect(item)}>
            {isGroup
                ? <Avatar src={item.photo || undefined} name={primary} w="38px" h="38px" borderRadius="12px" />
                : <RowAvatar label={(primary || "?").trim().charAt(0).toUpperCase()} accent={accent} />}
            <Box flex="1" minW={0}>
                <Text fontSize="0.9rem" fontWeight={isSelected ? "700" : "600"} color={palette.name} isTruncated>{primary}</Text>
                <Text fontSize="0.78rem" color={palette.email} isTruncated>{secondary}</Text>
            </Box>
            {tag && (
                <Badge bg={tag.bg} color={tag.color} borderColor={tag.border} borderWidth="1px"
                    borderRadius="8px" px="8px" py="2px" fontSize="0.66rem" textTransform="none" flexShrink={0}>
                    {tag.label}
                </Badge>
            )}
        </Flex>
    );
}

/**
 * Non-row states: search prompt, spinner, error or empty result.
 * @param {Object} props - { mode, showSearchPrompt, loading, error, palette }
 * @returns {React.ReactElement}
 */
function TableState({ mode, showSearchPrompt, loading, error, palette }) {
    if (loading) return <Flex justify="center" py="40px"><Spinner color="brand.500" /></Flex>;
    if (error) return <Text color="red.500" textAlign="center" py="32px" fontSize="0.85rem">{typeof error === "string" ? error : "Erreur de chargement"}</Text>;
    if (showSearchPrompt) {
        return (
            <Flex direction="column" align="center" justify="center" py="40px" gap="10px">
                <Icon as={MdPersonSearch} boxSize="34px" color={palette.email} />
                <Text color={palette.email} fontSize="0.85rem" textAlign="center" px="16px">Recherchez un utilisateur par nom ou email</Text>
            </Flex>
        );
    }
    const emptyLabel = mode === "groups" ? "Aucun groupe trouvé" : "Aucun utilisateur trouvé";
    const emptyIcon = mode === "groups" ? MdGroups : MdSearch;
    return (
        <Flex direction="column" align="center" justify="center" py="40px" gap="10px">
            <Icon as={emptyIcon} boxSize="34px" color={palette.email} />
            <Text color={palette.email} fontSize="0.85rem" textAlign="center" px="16px">{emptyLabel}</Text>
        </Flex>
    );
}

/**
 * Scrollable list of users or groups (lightweight rows, max 10 results).
 * @param {object} props - { items, mode, loading, error, roleFilter, selectedId, onSelect, showSearchPrompt }
 * @returns {React.ReactElement}
 */
function UsersTable({ items, mode = "users", loading, error, roleFilter, selectedId, onSelect, showSearchPrompt }) {
    const palette = {
        name: useColorModeValue("secondaryGray.900", "white"),
        email: useColorModeValue("secondaryGray.600", "secondaryGray.400"),
        hoverBg: useColorModeValue("secondaryGray.100", "whiteAlpha.100"),
        selectedBg: useColorModeValue("brand.100", "whiteAlpha.200"),
    };
    const roleTag = TAB_ROLE_TAG[roleFilter] || null;
    const showRows = !loading && !error && items.length > 0;
    return (
        <Box maxH="calc(100vh - 320px)" minH="160px" overflowY="auto" mt="4px">
            {showRows
                ? items.map((item) => (
                    <EntityRow key={item.id} item={item} mode={mode} isSelected={selectedId === item.id}
                        onSelect={onSelect} roleTag={roleTag} palette={palette} />
                ))
                : <TableState mode={mode} showSearchPrompt={showSearchPrompt} loading={loading} error={error} palette={palette} />}
        </Box>
    );
}

export default UsersTable;
